// Hand-rolled CBOR for `EphemeralEnvelope` + `EphemeralBody`
// (`types-and-schemas.md` §4.1). Mirrors `transport-mls/cborEnvelope.ts`.

export interface EphemeralEnvelopeWire {
  readonly conversation_id: Uint8Array;
  readonly sender_device: Uint8Array;
  readonly epoch: number;
  readonly nonce_seq: number;
  readonly ciphertext: Uint8Array;
}

export interface EphemeralBodyWire {
  readonly type: string;
  readonly payload: unknown;
}

/* ------------------------------ encode helpers ------------------------------ */

function uintHeaderLen(n: number): number {
  if (n < 24) return 1;
  if (n < 0x100) return 2;
  if (n < 0x10000) return 3;
  if (n < 0x100000000) return 5;
  return 9;
}

function writeHeader(buf: Uint8Array, o: number, majorType: number, n: number): number {
  const base = majorType << 5;
  if (n < 24) {
    buf[o] = base | n;
    return o + 1;
  }
  if (n < 0x100) {
    buf[o] = base | 24;
    buf[o + 1] = n;
    return o + 2;
  }
  if (n < 0x10000) {
    buf[o] = base | 25;
    buf[o + 1] = (n >> 8) & 0xff;
    buf[o + 2] = n & 0xff;
    return o + 3;
  }
  if (n < 0x100000000) {
    buf[o] = base | 26;
    buf[o + 1] = (n >>> 24) & 0xff;
    buf[o + 2] = (n >>> 16) & 0xff;
    buf[o + 3] = (n >>> 8) & 0xff;
    buf[o + 4] = n & 0xff;
    return o + 5;
  }
  buf[o] = base | 27;
  const hi = Math.floor(n / 0x100000000);
  const lo = n >>> 0;
  buf[o + 1] = (hi >>> 24) & 0xff;
  buf[o + 2] = (hi >>> 16) & 0xff;
  buf[o + 3] = (hi >>> 8) & 0xff;
  buf[o + 4] = hi & 0xff;
  buf[o + 5] = (lo >>> 24) & 0xff;
  buf[o + 6] = (lo >>> 16) & 0xff;
  buf[o + 7] = (lo >>> 8) & 0xff;
  buf[o + 8] = lo & 0xff;
  return o + 9;
}

function writeUint(buf: Uint8Array, o: number, n: number): number {
  return writeHeader(buf, o, 0, n);
}

function writeBytes(buf: Uint8Array, o: number, bytes: Uint8Array): number {
  o = writeHeader(buf, o, 2, bytes.length);
  buf.set(bytes, o);
  return o + bytes.length;
}

function writeText(buf: Uint8Array, o: number, str: string): number {
  const encoded = new TextEncoder().encode(str);
  o = writeHeader(buf, o, 3, encoded.length);
  buf.set(encoded, o);
  return o + encoded.length;
}

function writeMapHeader(buf: Uint8Array, o: number, entries: number): number {
  return writeHeader(buf, o, 5, entries);
}

function encodedValueSize(value: unknown): number {
  if (value === null || value === undefined) {
    return 1; // null
  }
  if (typeof value === 'boolean') {
    return 1;
  }
  if (typeof value === 'number') {
    if (!Number.isInteger(value) || value < 0) {
      throw new Error('cbor encode: unsupported number');
    }
    return uintHeaderLen(value);
  }
  if (typeof value === 'string') {
    const e = new TextEncoder().encode(value);
    return uintHeaderLen(e.length) + e.length;
  }
  if (value instanceof Uint8Array) {
    return uintHeaderLen(value.length) + value.length;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const rec = value as Record<string, unknown>;
    const keys = Object.keys(rec);
    let size = 1;
    for (const k of keys) {
      size += uintHeaderLen(new TextEncoder().encode(k).length) + k.length;
      size += encodedValueSize(rec[k]);
    }
    return size;
  }
  throw new Error('cbor encode: unsupported value type');
}

function writeValue(buf: Uint8Array, o: number, value: unknown): number {
  if (value === null || value === undefined) {
    buf[o] = 0xf6; // null
    return o + 1;
  }
  if (typeof value === 'boolean') {
    buf[o] = value ? 0xf5 : 0xf4;
    return o + 1;
  }
  if (typeof value === 'number') {
    if (!Number.isInteger(value) || value < 0) {
      throw new Error('cbor encode: unsupported number');
    }
    return writeUint(buf, o, value);
  }
  if (typeof value === 'string') {
    return writeText(buf, o, value);
  }
  if (value instanceof Uint8Array) {
    return writeBytes(buf, o, value);
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const rec = value as Record<string, unknown>;
    const keys = Object.keys(rec);
    o = writeMapHeader(buf, o, keys.length);
    for (const k of keys) {
      o = writeText(buf, o, k);
      o = writeValue(buf, o, rec[k]);
    }
    return o;
  }
  throw new Error('cbor encode: unsupported value type');
}

function envelopeEncodedSize(env: EphemeralEnvelopeWire): number {
  let size = 1; // map(5)
  size += 1 + uintHeaderLen(env.conversation_id.length) + env.conversation_id.length;
  size += 1 + uintHeaderLen(env.sender_device.length) + env.sender_device.length;
  size += 1 + uintHeaderLen(env.epoch);
  size += 1 + uintHeaderLen(env.nonce_seq);
  size += 1 + uintHeaderLen(env.ciphertext.length) + env.ciphertext.length;
  return size;
}

export function encodeEphemeralEnvelope(env: EphemeralEnvelopeWire): Uint8Array {
  if (env.conversation_id.length !== 16) {
    throw new Error('ephemeral envelope: conversation_id must be 16 bytes');
  }
  if (env.sender_device.length !== 32) {
    throw new Error('ephemeral envelope: sender_device must be 32 bytes');
  }
  const size = envelopeEncodedSize(env);
  const buf = new Uint8Array(size);
  let o = 0;
  o = writeMapHeader(buf, o, 5);
  o = writeUint(buf, o, 1);
  o = writeBytes(buf, o, env.conversation_id);
  o = writeUint(buf, o, 2);
  o = writeBytes(buf, o, env.sender_device);
  o = writeUint(buf, o, 3);
  o = writeUint(buf, o, env.epoch);
  o = writeUint(buf, o, 4);
  o = writeUint(buf, o, env.nonce_seq);
  o = writeUint(buf, o, 5);
  o = writeBytes(buf, o, env.ciphertext);
  if (o !== size) {
    throw new Error(`cbor ephemeral envelope size mismatch: predicted=${size} wrote=${o}`);
  }
  return buf;
}

function bodyEncodedSize(body: EphemeralBodyWire): number {
  const typeBytes = new TextEncoder().encode(body.type);
  let size = 1; // map(2)
  size += 1; // key 1 (uint)
  size += uintHeaderLen(typeBytes.length) + typeBytes.length;
  size += 1; // key 2 (uint)
  size += encodedValueSize(body.payload);
  return size;
}

export function encodeEphemeralBody(body: EphemeralBodyWire): Uint8Array {
  const size = bodyEncodedSize(body);
  const buf = new Uint8Array(size);
  let o = 0;
  o = writeMapHeader(buf, o, 2);
  o = writeUint(buf, o, 1);
  o = writeText(buf, o, body.type);
  o = writeUint(buf, o, 2);
  o = writeValue(buf, o, body.payload);
  if (o !== size) {
    throw new Error(`cbor ephemeral body size mismatch: predicted=${size} wrote=${o}`);
  }
  return buf;
}

/* ------------------------------ decode ------------------------------ */

interface Cursor {
  buf: Uint8Array;
  o: number;
}

function readHeader(c: Cursor): { major: number; n: number } {
  const first = c.buf[c.o++];
  if (first === undefined) throw new Error('cbor decode: unexpected eof');
  const major = first >> 5;
  const info = first & 0x1f;
  if (info < 24) return { major, n: info };
  if (info === 24) {
    const n = c.buf[c.o++];
    if (n === undefined) throw new Error('cbor decode: short uint8');
    return { major, n };
  }
  if (info === 25) {
    const a = c.buf[c.o++];
    const b = c.buf[c.o++];
    if (a === undefined || b === undefined) throw new Error('cbor decode: short uint16');
    return { major, n: (a << 8) | b };
  }
  if (info === 26) {
    const a = c.buf[c.o++]!;
    const b = c.buf[c.o++]!;
    const d = c.buf[c.o++]!;
    const e = c.buf[c.o++]!;
    return { major, n: ((a << 24) | (b << 16) | (d << 8) | e) >>> 0 };
  }
  if (info === 27) {
    const parts: number[] = [];
    for (let i = 0; i < 8; i++) {
      const p = c.buf[c.o++];
      if (p === undefined) throw new Error('cbor decode: short uint64');
      parts.push(p);
    }
    const hi = ((parts[0]! << 24) | (parts[1]! << 16) | (parts[2]! << 8) | parts[3]!) >>> 0;
    const lo = ((parts[4]! << 24) | (parts[5]! << 16) | (parts[6]! << 8) | parts[7]!) >>> 0;
    return { major, n: hi * 0x100000000 + lo };
  }
  throw new Error(`cbor decode: unsupported additional info ${info}`);
}

function readBytes(c: Cursor): Uint8Array {
  const h = readHeader(c);
  if (h.major !== 2) throw new Error(`cbor decode: expected bytes, got major ${h.major}`);
  const slice = c.buf.slice(c.o, c.o + h.n);
  c.o += h.n;
  return slice;
}

function readText(c: Cursor): string {
  const h = readHeader(c);
  if (h.major !== 3) throw new Error(`cbor decode: expected text, got major ${h.major}`);
  const slice = c.buf.slice(c.o, c.o + h.n);
  c.o += h.n;
  return new TextDecoder().decode(slice);
}

function readUint(c: Cursor): number {
  const h = readHeader(c);
  if (h.major !== 0) throw new Error(`cbor decode: expected uint, got major ${h.major}`);
  return h.n;
}

function readValue(c: Cursor): unknown {
  const peek = c.buf[c.o];
  if (peek === undefined) throw new Error('cbor decode: unexpected eof');
  const major = peek >> 5;
  if (major === 0) return readUint(c);
  if (major === 2) return readBytes(c);
  if (major === 3) return readText(c);
  if (major === 5) {
    const mapLen = readHeader(c).n;
    const out: Record<string, unknown> = {};
    for (let i = 0; i < mapLen; i++) {
      const key = readText(c);
      out[key] = readValue(c);
    }
    return out;
  }
  if (peek === 0xf4) {
    c.o++;
    return false;
  }
  if (peek === 0xf5) {
    c.o++;
    return true;
  }
  if (peek === 0xf6) {
    c.o++;
    return null;
  }
  throw new Error(`cbor decode: unsupported value major ${major}`);
}

export function decodeEphemeralEnvelope(wire: Uint8Array): EphemeralEnvelopeWire {
  const c: Cursor = { buf: wire, o: 0 };
  const mapH = readHeader(c);
  if (mapH.major !== 5) throw new Error('cbor decode: expected map for envelope');

  let conversation_id: Uint8Array | undefined;
  let sender_device: Uint8Array | undefined;
  let epoch: number | undefined;
  let nonce_seq: number | undefined;
  let ciphertext: Uint8Array | undefined;

  for (let i = 0; i < mapH.n; i++) {
    const key = readUint(c);
    switch (key) {
      case 1:
        conversation_id = readBytes(c);
        break;
      case 2:
        sender_device = readBytes(c);
        break;
      case 3:
        epoch = readUint(c);
        break;
      case 4:
        nonce_seq = readUint(c);
        break;
      case 5:
        ciphertext = readBytes(c);
        break;
      default:
        readValue(c);
        break;
    }
  }

  if (
    conversation_id === undefined ||
    sender_device === undefined ||
    epoch === undefined ||
    nonce_seq === undefined ||
    ciphertext === undefined
  ) {
    throw new Error('cbor decode: incomplete ephemeral envelope');
  }
  return { conversation_id, sender_device, epoch, nonce_seq, ciphertext };
}

export function decodeEphemeralBody(wire: Uint8Array): EphemeralBodyWire {
  const c: Cursor = { buf: wire, o: 0 };
  const mapH = readHeader(c);
  if (mapH.major !== 5) throw new Error('cbor decode: expected map for body');

  let type: string | undefined;
  let payload: unknown;

  for (let i = 0; i < mapH.n; i++) {
    const key = readUint(c);
    if (key === 1) {
      type = readText(c);
    } else if (key === 2) {
      payload = readValue(c);
    } else {
      readValue(c);
    }
  }

  if (type === undefined) {
    throw new Error('cbor decode: ephemeral body missing type');
  }
  return { type, payload: payload ?? {} };
}

export function parseRoutingFields(wire: Uint8Array): {
  conversation_id: Uint8Array;
  sender_device: Uint8Array;
} {
  const env = decodeEphemeralEnvelope(wire);
  return {
    conversation_id: env.conversation_id,
    sender_device: env.sender_device,
  };
}
